import * as React from 'react';
import { WifiOffLine, LoadingLine } from '@mingcute/react';

import { cn } from '../lib/cn';

export type ConnectionState = 'offline' | 'reconnecting' | 'online';

export interface ConnectionBannerProps extends React.HTMLAttributes<HTMLDivElement> {
  state: ConnectionState;
  offlineMessage?: string;
  reconnectingMessage?: string;
}

export const ConnectionBanner = React.forwardRef<HTMLDivElement, ConnectionBannerProps>(
  (
    {
      className,
      state,
      offlineMessage = 'You are offline. Changes will sync when connection is restored.',
      reconnectingMessage = 'Reconnecting…',
      ...props
    },
    ref,
  ) => {
    if (state === 'online') return null;
    const isReconnecting = state === 'reconnecting';
    return (
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        className={cn(
          'sticky top-0 z-[var(--z-sticky)] flex w-full items-center justify-center gap-2 px-4 py-1.5 text-xs font-medium',
          isReconnecting
            ? 'bg-[var(--color-warning)] text-[var(--color-neutral-900)]'
            : 'bg-[var(--color-danger)] text-[var(--tvl-on-accent)]',
          'animate-in fade-in slide-in-from-top-2',
          className,
        )}
        {...props}
      >
        {isReconnecting ? (
          <LoadingLine size={14} className="animate-spin" aria-hidden />
        ) : (
          <WifiOffLine size={14} aria-hidden />
        )}
        <span>{isReconnecting ? reconnectingMessage : offlineMessage}</span>
      </div>
    );
  },
);
ConnectionBanner.displayName = 'ConnectionBanner';
